import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

//========== Import components from MUI Library ============
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";

//All Loading related components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

//All CSS
import { makeStyles } from "tss-react/mui";
//All Element/ Controls
import CollapseRow from "./CollapseRow";
import CommonLayout from "../../../Common/Layout";
import { ButtonUI, ConfirmPopup } from "../../../Common";
import { findRelations, setPrevState } from "../../../../util/common";
import WorkspaceClass from "../../../../service/workspace";
import ImportHistory from "./ImportHistory";
import { showToast } from "../../../../store/actions";
import UploadFile from "./uploadFile";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import SmallToolbar from "../../../Common/SmallToolbar";

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 4,
    display: "inline-flex",
  },
  subContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    borderTop: 0,
  },
  PanelContainer: {
    padding: "8px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    "& .MuiAlert-message .MuiList-root": {
      listStyle: "square",
    },
  },
  Panel: {
    borderRadius: 10,
    marginBottom: 0,
  },
  SchemaLabel: {
    padding: 0,
    margin: 0,
    paddingBottom: "15px",
    color: "black",
    fontWeight: 600,
    fontSize: "1rem",
    border: "none",
  },
}));

const ImportWorkspace = () => {
  const { Workspace_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [compare, setCompare] = useState(false);
  const [disableList, setDisableList] = useState({
    checkBoxes: [],
    subSection: [],
  });
  const [options, setOptions] = useState([]);
  const [behaviourList, setBehaviourList] = useState([]);

  const [workspaceList, setWorkspaceList] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState({});
  const [submitData, setSubmitData] = useState({});

  const [checkedValues, setCheckedValues] = useState({});
  const [comparisonList, setComparisonList] = useState({});

  const [configState, setConfigState] = useState({
    uploadedFile: null,
    file: "",
    importFrom: 0,
    selectedWorkspace: "",
    importBehavior: "newOnly",
    importSecretKey: "",
    openConfirm: false,
    openSummary: false,
    loadingForConfirm: false,
  });

  const { importFrom, importBehavior, selectedWorkspace } = configState;

  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const resetCheckboxes = useCallback((options = []) => {
    let resetCheckedValues = {};
    options.forEach(
      ({ value }) =>
        (resetCheckedValues[value] = {
          checked: false,
          new: [],
          modified: [],
        })
    );

    return resetCheckedValues;
  }, []);

  const resetCommonOptions = useCallback(() => {
    setCompare(false);
    setDisableList({ checkBoxes: [], subSection: [] });
    setCheckedValues(resetCheckboxes(options));
    setComparisonList({});
    setSubmitData({});
  }, [options, resetCheckboxes]);

  const handleImportSummaryClose = useCallback(() => {
    resetCommonOptions();
    setPrevState(setConfigState, {
      uploadedFile: null,
      file: "",
      importFrom: 0,
      selectedWorkspace: "",
      openConfirm: false,
      loadingForConfirm: false,
      importBehavior: "newOnly",
      importSecretKey: "",
      openSummary: false,
    });
  }, [resetCommonOptions]);

  const prepareResultingObject = useCallback(
    (resultingObject, relatedItems) => {
      const autoSelection = relatedItems?.length
        ? findRelations(
            relatedItems,
            [
              ...(comparisonList["datasources"]["new"]?.data ?? []),
              ...(comparisonList["datasources"]["modified"]?.data ?? []),
            ],
            [
              ...(comparisonList["entities"]["new"]?.data ?? []),
              ...(comparisonList["entities"]["modified"]?.data ?? []),
            ],
            [
              ...(comparisonList["connections"]["new"]?.data ?? []),
              ...(comparisonList["connections"]["modified"]?.data ?? []),
            ],
            [
              ...(comparisonList["pipelines"]["new"]?.data ?? []),
              ...(comparisonList["pipelines"]["modified"]?.data ?? []),
            ]
          )
        : {};

      options?.forEach(({ value }) => {
        const checkedItems = {
          new: [
            ...new Set([
              ...resultingObject[value]["new"],
              ...(
                comparisonList[value]["new"]?.data?.filter(
                  (option) => autoSelection?.[value]?.includes(option?.value) || false
                ) || []
              ).map(({ value }) => value),
            ]),
          ],
          modified: [
            ...new Set([
              ...resultingObject[value]["modified"],
              ...(
                comparisonList[value]["modified"]?.data?.filter(
                  (option) => autoSelection?.[value]?.includes(option?.value) || false
                ) || []
              ).map(({ value }) => value),
            ]),
          ],
        };

        resultingObject[value] = {
          checked: Boolean(checkedItems?.new?.length + checkedItems?.modified?.length),
          ...checkedItems,
        };
      });

      return resultingObject;
    },
    [comparisonList, options]
  );

  const handleParentCheck = useCallback(
    (e) => {
      let { value } = e.target;
      if (e.target.checked) {
        // All First level related items for current selected parent value
        // this will get relations of all the items under this section
        const relatedItems = [
          ...(comparisonList[value]?.new?.data?.flatMap((item) => item?.relations || []) || []),
          ...(comparisonList[value]?.modified?.data?.flatMap((item) => item?.relations || []) || []),
        ];

        setCheckedValues((prev) =>
          prepareResultingObject(
            {
              ...prev,
              [value]: compare
                ? {
                    new: comparisonList[value]["new"]["data"].map(({ value }) => value),
                    modified: comparisonList[value]["modified"]["data"].map(({ value }) => value),
                    checked: true,
                  }
                : { checked: true, new: [], modified: [] },
            },
            compare ? relatedItems : []
          )
        );
      } else {
        setCheckedValues((prev) => ({
          ...prev,
          [value]: { checked: false, new: [], modified: [] },
        }));
      }
    },
    [compare, comparisonList, prepareResultingObject]
  );

  const handleChildCheck = useCallback(
    (e, parent, type) => {
      let { value } = e.target;
      // All First level related items for current selected value
      const relatedItems =
        (comparisonList[parent][type]?.data || [])?.find(({ value: compareValue }) => compareValue === value)
          ?.relations || [];

      if (e.target.checked) {
        setCheckedValues((prev) =>
          prepareResultingObject(
            {
              ...prev,
              [parent]: { ...prev[parent], checked: true, [type]: [...prev[parent][type], value] },
            },
            relatedItems
          )
        );
      } else if (checkedValues[parent]["new"].length + checkedValues[parent]["modified"].length === 1) {
        setCheckedValues((prev) => ({
          ...prev,
          [parent]: { checked: false, new: [], modified: [] },
        }));
      } else {
        let updatedList = checkedValues[parent][type].filter((val) => val.toLowerCase() !== value.toLowerCase());
        setCheckedValues((prev) =>
          prepareResultingObject(
            {
              ...prev,
              [parent]: { ...prev[parent], [type]: updatedList },
            },
            relatedItems?.map((id) => updatedList?.includes(id))
          )
        );
      }
    },
    [checkedValues, comparisonList, prepareResultingObject]
  );

  const handleCompare = useCallback(async () => {
    setLoading(true);
    const workspace = new WorkspaceClass();
    let submitData = { importFrom: importFrom };
    const formData = new FormData();

    if (importFrom === 0) {
      if (selectedWorkspace !== "") {
        submitData.Compare_id = selectedWorkspace.value;
        submitData.selectedWorkspace = selectedWorkspace;
      }
    } else {
      // Submit upload file data using Formdata
      formData.append("file", configState.uploadedFile);
      formData.append("fileName", configState.file);
      formData.append("importSecretKey", configState.importSecretKey);
    }

    const response = await workspace.compareWorkspaces(
      Workspace_id,
      importFrom,
      importFrom === 0 ? submitData : formData
    );

    if (response.success) {
      setComparisonList(response.data || {});
      const checkboxesSet = new Set();
      const subSectionSet = new Set();

      options.forEach((option) => {
        const newCount = response.data[option.value]["new"]["count"];
        const modifiedCount = response.data[option.value]["modified"]["count"];
        if (importBehavior === "newOnly") {
          if (newCount === 0) checkboxesSet.add(option.value);
        } else if (importBehavior === "addAndOverwrite") {
          if (newCount + modifiedCount === 0) checkboxesSet.add(option.value);
        }
        if (newCount + modifiedCount === 0) subSectionSet.add(option.value);
      });

      let updatedObj = {};
      Object.keys(checkedValues).forEach((option) => {
        updatedObj[option] = {
          checked: checkedValues[option].checked,
          new: checkedValues[option].checked ? response.data[option]["new"]["data"].map(({ value }) => value) : [],
          modified: checkedValues[option].checked
            ? response.data[option]["modified"]["data"].map(({ value }) => value)
            : [],
        };
      });

      if (
        importFrom === 1 &&
        configState.importSecretKey.trim().length === 0 &&
        (!checkboxesSet.has("connections") || !subSectionSet.has("connections"))
      ) {
        checkboxesSet.add("connections");
        subSectionSet.add("connections");
        updatedObj["connections"] = { checked: false, new: [], modified: [] };
      }

      setPrevState(setConfigState, {
        checkedValues: updatedObj,
      });

      setDisableList({ checkBoxes: [...checkboxesSet], subSection: [...subSectionSet] });
      setCompare(true);
    }
    setLoading(false);
  }, [
    Workspace_id,
    checkedValues,
    configState.file,
    configState.importSecretKey,
    configState.uploadedFile,
    importBehavior,
    importFrom,
    options,
    selectedWorkspace,
  ]);

  const handleImport = useCallback(async () => {
    setPrevState(setConfigState, { loadingForConfirm: true });
    const formData = new FormData();
    let submitData = {
      importFrom: importFrom,
      importBehavior: importBehavior,
      importFromWorkspace: selectedWorkspace,
    };

    options.forEach(({ value }) => {
      if (
        checkedValues[value].checked &&
        (importBehavior === "newOnly"
          ? checkedValues[value].new.length !== 0
          : checkedValues[value].new.length + checkedValues[value].modified.length !== 0) &&
        !disableList.checkBoxes.includes(value)
      ) {
        submitData[value] = {
          ...(importBehavior === "newOnly"
            ? { new: checkedValues[value].new }
            : { new: checkedValues[value].new, modified: checkedValues[value].modified }),
        };
      }
    });

    if (importFrom === 0) {
      if (selectedWorkspace !== "") submitData.ImportFromWorkspace_id = selectedWorkspace.value;
    } else if (importFrom === 1) {
      // Submit upload file data using Formdata
      formData.append("file", configState.uploadedFile);
      formData.append("fileName", configState.file);
      formData.append("importSecretKey", configState.importSecretKey);
      for (let property in submitData) {
        formData.append(
          property,
          typeof submitData[property] === "string" ? submitData[property] : JSON.stringify(submitData[property])
        );
      }
    }

    setSubmitData(configState.importFrom === 0 ? submitData : formData);
    setPrevState(setConfigState, {
      openConfirm: false,
      loadingForConfirm: false,
      openSummary: true,
    });
  }, [
    checkedValues,
    configState.file,
    configState.importFrom,
    configState.importSecretKey,
    configState.uploadedFile,
    disableList.checkBoxes,
    importBehavior,
    importFrom,
    options,
    selectedWorkspace,
  ]);

  const handleWorkspaceChange = useCallback(
    (e) => {
      resetCommonOptions();
      setPrevState(setConfigState, { selectedWorkspace: e.target.value });
    },
    [resetCommonOptions]
  );

  const uploadFile = useCallback(
    (e) => {
      const fileData = e.target.files[0];
      e.target.value = "";

      if (fileData && fileData.name.lastIndexOf(".") > 0) {
        let fileExtension = fileData.name.substring(fileData.name.lastIndexOf(".") + 1, fileData.name.length);
        if (fileExtension.toLowerCase() !== "json") {
          dispatch(
            showToast({
              message: "File type not supported.",
              type: "error",
            })
          );
        } else {
          setPrevState(setConfigState, { uploadedFile: fileData, file: fileData.name });
        }
      }
      let resetCheckedValues = resetCheckboxes(options);
      setCompare(false);
      setDisableList({ checkBoxes: [], subSection: [] });
      setCheckedValues(resetCheckedValues);
      setPrevState(setConfigState, { importSecretKey: "" });
    },
    [dispatch, options, resetCheckboxes]
  );

  const handleImportFromChange = useCallback(
    (e) => {
      resetCommonOptions();
      setPrevState(setConfigState, {
        importFrom: Number(e.target.value),
        importSecretKey: "",
        selectedWorkspace: "",
        uploadedFile: null,
        file: "",
      });
    },
    [resetCommonOptions]
  );

  const handleImportBehaviorChange = useCallback(
    (e) => {
      setPrevState(setConfigState, {
        importBehavior: e.target.value,
      });
      const checkboxesSet = new Set();
      const subSectionSet = new Set();

      options.forEach((option) => {
        if (comparisonList[option.value]) {
          const newCount = comparisonList[option.value]["new"].count;
          const modifiedCount = comparisonList[option.value]["modified"].count;
          if (e.target.value === "newOnly") {
            if (newCount === 0) checkboxesSet.add(option.value);
          } else if (e.target.value === "addAndOverwrite") {
            if (newCount + modifiedCount === 0) checkboxesSet.add(option.value);
          }
          if (newCount + modifiedCount === 0) subSectionSet.add(option.value);
        }
      });

      if (
        importFrom === 1 &&
        configState.importSecretKey.trim().length === 0 &&
        (!checkboxesSet.has("connections") || !subSectionSet.has("connections"))
      ) {
        checkboxesSet.add("connections");
        subSectionSet.add("connections");
      }

      setDisableList({ checkBoxes: [...checkboxesSet], subSection: [...subSectionSet] });
    },
    [comparisonList, configState.importSecretKey, importFrom, options]
  );

  const ConfirmMessage = useCallback(() => {
    return (
      <Box sx={{ maxWidth: "500px" }}>
        <Typography component={"span"}>
          {`Are you sure you want to Import the selected configuration(s) from ${
            importFrom === 0 ? `"Workspace: ${selectedWorkspace.name}"` : `"File: ${configState.file}"`
          }?`}
        </Typography>
      </Box>
    );
  }, [configState.file, importFrom, selectedWorkspace.name]);

  const ImportTableSection = useCallback(() => {
    const disabledCheckboxes = !Object.keys(comparisonList).length;

    return (
      <>
        {compare
          ? options.map((option, i) => (
              <CollapseRow
                key={option.value + i}
                option={option}
                importFrom={importFrom}
                newItems={comparisonList[option.value]["new"]}
                modifiedItems={comparisonList[option.value]["modified"]}
                disableList={disableList}
                checkedValues={checkedValues}
                handleParentCheck={handleParentCheck}
                handleChildCheck={handleChildCheck}
                importBehavior={importBehavior}
                importSecretKey={configState.importSecretKey}
              />
            ))
          : options.map((option, index) => (
              <TableRow key={option?.value + index}>
                <TableCell sx={{ border: "none", padding: 0 }}>
                  <FormControlLabel
                    value={option.value}
                    control={
                      <Checkbox
                        checked={checkedValues[option.value].checked && !disableList.checkBoxes.includes(option.value)}
                      />
                    }
                    label={option.text}
                    disabled={disabledCheckboxes || disableList.checkBoxes.includes(option.value)}
                    onChange={handleParentCheck}
                    sx={{ margin: 0 }}
                  />
                </TableCell>
              </TableRow>
            ))}
      </>
    );
  }, [
    checkedValues,
    compare,
    comparisonList,
    configState.importSecretKey,
    disableList,
    handleChildCheck,
    handleParentCheck,
    importBehavior,
    importFrom,
    options,
  ]);

  const configurarionTable = useCallback(() => {
    const comparingWith = importFrom === 0 ? selectedWorkspace.name : configState.file;

    return (
      <Grid sx={{ padding: "15px 15px 15px 30px" }} container>
        <Grid sx={{ display: "flex", alignItems: "center" }} size={12}>
          <TableContainer sx={{ width: "100%" }} size="small">
            <Table
              sx={{
                width: "100%",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      minWidth: "180px",
                      width: "290px",
                      maxWidth: "290px",
                    }}
                    className={classes.SchemaLabel}
                  >
                    Import
                  </TableCell>
                  <TableCell className={classes.SchemaLabel}>
                    {compare ? `Comparing "${currentWorkspace.Name}" & "${comparingWith}"` : ""}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{ImportTableSection()}</TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  }, [
    ImportTableSection,
    classes.SchemaLabel,
    compare,
    configState.file,
    currentWorkspace.Name,
    importFrom,
    selectedWorkspace.name,
  ]);

  const ImportSection = useCallback(() => {
    return (
      <Grid sx={{ padding: "15px 15px 15px 30px" }} container spacing={2}>
        <Grid sx={{ display: "flex", alignItems: "center" }} size={12}>
          <RadioGroup value={importFrom} onChange={(e) => handleImportFromChange(e)} row>
            <FormControlLabel value={0} control={<Radio />} label="From Other Workspace" />
            <FormControlLabel value={1} control={<Radio />} label="From Configuration File" />
          </RadioGroup>
        </Grid>
        <Grid container sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          {importFrom === 0 ? (
            <Box sx={{ width: "100%" }}>
              <TextField
                fullWidth
                label="Select Workspace"
                value={selectedWorkspace}
                onChange={handleWorkspaceChange}
                required
                select
                sx={{ maxWidth: "350px" }}
              >
                <MenuItem value="">Select Workspace</MenuItem>
                {workspaceList.map((ws, i) =>
                  !currentWorkspace.IsDataVaultEnabled && ws.IsDataVaultEnabled ? (
                    <Tooltip title="DataVault 2.0 Workspace" placement="right" key={ws.value + i}>
                      <span>
                        <MenuItem key={ws.value} value={ws} disabled={true}>
                          {ws.name}
                        </MenuItem>
                      </span>
                    </Tooltip>
                  ) : (
                    <MenuItem key={ws.value} value={ws}>
                      {ws.name}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Box>
          ) : (
            <Box sx={{ padding: "15px 15px 15px 30px", width: "100%" }}>
              <UploadFile
                configState={configState}
                setCheckedValues={setCheckedValues}
                setConfigState={setConfigState}
                uploadFile={uploadFile}
                setDisableList={setDisableList}
                compare={compare}
                comparisonList={comparisonList}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    );
  }, [
    compare,
    configState,
    currentWorkspace.IsDataVaultEnabled,
    handleImportFromChange,
    handleWorkspaceChange,
    importFrom,
    selectedWorkspace,
    uploadFile,
    workspaceList,
    comparisonList,
  ]);

  useEffect(() => {
    const loadWorkspaceList = async () => {
      setLoading(true);
      const workspace = new WorkspaceClass();
      const response = await workspace.getImportWorkspaceDetails(Workspace_id);
      if (response.success) {
        // This will reset checkboxes as per the options available
        setCheckedValues(resetCheckboxes(response.data.comparisonOptionList));

        setWorkspaceList(response.data.workspaceList || []);
        setCurrentWorkspace(response.data.workspace || {});
        setOptions(response.data.comparisonOptionList);
        setBehaviourList(response.data.importBehaviourList);
      }
      setLoading(false);
    };

    loadWorkspaceList();
    return () => {};
  }, [Workspace_id, navigate, resetCheckboxes]);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: `/my-workspaces/${Workspace_id}`, displayName: currentWorkspace.Name, active: false },
      {
        link: "#",
        displayName: "Import Workspace Configuration",
        active: true,
      },
    ],
    [Workspace_id, currentWorkspace.Name]
  );

  return (
    <CommonLayout title="Settings" loading={loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <AppBar
          sx={{
            position: "relative",
            boxShadow: 0,
            backgroundColor: "#e8edf1",
          }}
        >
          <Toolbar className={classes.Header}>
            <Grid container direction="row" alignItems={"center"}>
              <Grid
                sx={{ display: "flex", alignItems: "center" }}
                size={{
                  sm: 12,
                  xs: 12,
                }}
              >
                <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                  <Typography className={classes.HeaderTitle} component="div">
                    Import Workspace Configuration
                  </Typography>
                </Box>
                <ButtonUI
                  variant="contained"
                  color="success"
                  sx={{ minWidth: "75px", ml: "12px" }}
                  onClick={() => setPrevState(setConfigState, { openConfirm: true })}
                  disabled={
                    loading ||
                    (importFrom === 0 && selectedWorkspace === "") ||
                    (importFrom === 1 && (configState.file === "" || !configState.uploadedFile)) ||
                    !Object.keys(checkedValues).some(
                      (key) =>
                        checkedValues[key].checked === true &&
                        (importBehavior === "newOnly"
                          ? checkedValues[key].new.length !== 0
                          : checkedValues[key].new.length + checkedValues[key].modified.length !== 0)
                    ) ||
                    !compare
                  }
                >
                  Import
                </ButtonUI>
                <Box>
                  <HelpContextIcon screen="Import Workspace" />
                </Box>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          <Box maxWidth="lg" className={classes.subContainer}>
            <Grid container direction="column" justifyContent="flex-end" alignItems="left">
              <Grid className={classes.PanelContainer}>
                <SmallToolbar>Source</SmallToolbar>
                <Paper elevation={1} className={classes.Panel}>
                  {ImportSection()}
                </Paper>
              </Grid>
            </Grid>
          </Box>
          <Box maxWidth="lg" className={classes.subContainer}>
            <Grid container direction="column" justifyContent="flex-end" alignItems="left">
              <Grid className={classes.PanelContainer}>
                <Grid
                  container
                  direction="row"
                  alignItems="left"
                  sx={{
                    alignContent: "baseline",
                  }}
                >
                  <Grid size={8}>
                    <SmallToolbar>Import Workspace Configuration</SmallToolbar>
                    <Box sx={{ pl: "5px", pb: "10px" }}>
                      <Typography sx={{ fontSize: "0.9rem", fontStyle: "italic" }}>
                        Import all the related items at once for a consistent update to your existing workspace.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid sx={{ pt: "20px", pb: "5px", textAlign: "right" }} size={4}>
                    <ButtonUI
                      variant="contained"
                      color="info"
                      sx={{ ml: "auto" }}
                      onClick={handleCompare}
                      disabled={
                        compare || (importFrom === 0 ? selectedWorkspace === "" : configState.file.trim() === "")
                      }
                    >
                      Compare Workspaces
                    </ButtonUI>
                  </Grid>
                </Grid>
                <Paper elevation={1} className={classes.Panel}>
                  {configurarionTable()}
                </Paper>
              </Grid>
            </Grid>
          </Box>
          <Box maxWidth="lg" className={classes.subContainer} sx={{ mb: 2 }}>
            <Grid container direction="column" justifyContent="flex-end" alignItems="left">
              <Grid className={classes.PanelContainer}>
                <SmallToolbar>Config Behavior</SmallToolbar>
                <Paper elevation={1} className={classes.Panel} sx={{ minHeight: "100px" }}>
                  <RadioGroup
                    row
                    name="type"
                    value={importBehavior}
                    onChange={handleImportBehaviorChange}
                    sx={{ padding: "15px 40px 15px 40px" }}
                  >
                    {behaviourList.map((behaviour, i) => (
                      <FormControlLabel
                        key={behaviour?.value + i}
                        value={behaviour.value}
                        control={<Radio />}
                        label={behaviour.text}
                        disabled={behaviour.value === "mergeAll"}
                        sx={{ width: "100%" }}
                      />
                    ))}
                  </RadioGroup>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
      {configState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Import Workspace Configuration",
            popupMessage: <ConfirmMessage></ConfirmMessage>,
            handleCancel: () => setPrevState(setConfigState, { openConfirm: false }),
            handleClick: () => handleImport(),
            loadingForConfirm: configState.loadingForConfirm,
            confirmBtnName: "Import",
          }}
        />
      )}
      {configState.openSummary && (
        <ImportHistory
          importFrom={importFrom}
          configState={configState}
          submitData={submitData}
          setConfigState={setConfigState}
          popupTitle={"Import Summary"}
          handleImportSummaryClose={handleImportSummaryClose}
        />
      )}
    </CommonLayout>
  );
};

export default ImportWorkspace;
